import {Label, RelativeTime, Truncate} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {ReleaseIcon, TagIcon} from '@primer/octicons-react'
import type {SidebarReleasesProps} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarLink} from './SidebarLink'
import {numberWithDelimiter} from '../utils'
import styles from './SidebarReleases.module.css'

/** Renders the Releases section of the repository sidebar. */
export function SidebarReleases({
  latestRelease,
  releaseCount,
  tagCount,
  releasesPath,
  tagsPath,
  canWrite,
  newReleasePath,
}: SidebarReleasesProps) {
  const moreReleaseCount = releaseCount - 1

  return (
    <SidebarSection
      title="Releases"
      href={releasesPath}
      count={latestRelease ? releaseCount : undefined}
      moreCount={latestRelease && releaseCount > 1 ? moreReleaseCount : undefined}
      moreLabel={`release${moreReleaseCount === 1 ? '' : 's'}`}
    >
      {latestRelease ? (
        <SidebarLink
          to={latestRelease.path}
          className={styles.releaseLink}
          analyticsLabel="section:releases;ref:latest"
        >
          <ReleaseIcon className={styles.releaseIconSuccess} />
          <div className={styles.releaseInfo}>
            <div className={styles.releaseNameRow}>
              <Truncate as="span" title={latestRelease.name} className={styles.releaseName} maxWidth="none">
                {latestRelease.name}
              </Truncate>
              <Label variant="success" className={styles.latestLabel}>
                Latest
              </Label>
            </div>
            <div className={styles.releaseMeta}>
              {latestRelease.publishedAt && <RelativeTime date={new Date(latestRelease.publishedAt)} tense="past" />}
            </div>
          </div>
        </SidebarLink>
      ) : (
        <>
          {tagCount > 0 ? (
            <SidebarLink to={tagsPath} className={styles.tagsLink} analyticsLabel="section:tags">
              <TagIcon className={styles.tagIconMuted} />
              <span className={styles.tagCount}>{numberWithDelimiter(tagCount)}</span>
              <span className={styles.tagLabel}>{`tag${tagCount === 1 ? '' : 's'}`}</span>
            </SidebarLink>
          ) : (
            <div className={styles.emptyState}>
              <span>No releases published</span>
            </div>
          )}
          {canWrite && newReleasePath && (
            <div className={`${styles.emptyState} ${tagCount > 0 ? styles.moreLink : ''}`}>
              <SidebarLink to={newReleasePath} inline analyticsLabel="section:releases;ref:create">
                <span>Create a new release</span>
              </SidebarLink>
            </div>
          )}
        </>
      )}
    </SidebarSection>
  )
}

export function SidebarReleasesSkeleton() {
  return (
    <SidebarSection title="Releases">
      <SkeletonText />
      <SkeletonText />
    </SidebarSection>
  )
}
